/**
 * AlexaAuth
 * OAuth authorization page for Alexa account linking.
 * Alexa redirects here → user signs in → we write auth code to Firestore → redirect back to Alexa.
 *
 * Auth code is written directly from the authenticated client to Firestore,
 * avoiding a Cloud Function call which is unreliable in Alexa's WebView.
 * Alexa then calls alexaToken Cloud Function to exchange the code for the UID.
 */

import { useState, useEffect, useRef } from 'react';
import { doc, setDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import { StorageAdapter } from '@/lib/storage-adapter';

const STORAGE_KEY = 'alexa_oauth_params';

const AlexaAuth = () => {
  const { user, loading, signInWithGoogle } = useAuth();
  const [status, setStatus] = useState<'idle' | 'linking' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const linkedRef = useRef(false);

  // Read params from URL — save to localStorage so they survive Google redirect
  const urlParams = new URLSearchParams(window.location.search);
  const urlRedirectUri = urlParams.get('redirect_uri');
  const urlState = urlParams.get('state');

  useEffect(() => {
    if (urlRedirectUri && urlState) {
      StorageAdapter.setItem(STORAGE_KEY, JSON.stringify({ redirectUri: urlRedirectUri, state: urlState }));
    }
  }, []);

  const getParams = (): { redirectUri: string; state: string } | null => {
    if (urlRedirectUri && urlState) return { redirectUri: urlRedirectUri, state: urlState };
    try {
      const stored = StorageAdapter.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return null;
  };

  const linkAccount = async () => {
    if (!user || linkedRef.current) return;
    const params = getParams();
    if (!params) {
      setStatus('error');
      setErrorMsg('Missing Alexa linking parameters. Please close this and try again from the Alexa app.');
      return;
    }

    linkedRef.current = true;
    setStatus('linking');

    try {
      // Generate auth code and write directly to Firestore (authenticated client)
      const code = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      await setDoc(doc(db, 'alexa_auth_codes', code), {
        uid: user.uid,
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 10 * 60 * 1000)), // 10 min
      });

      StorageAdapter.removeItem(STORAGE_KEY);
      setStatus('done');
      // Redirect back to Alexa with the auth code
      window.location.href = `${params.redirectUri}?code=${encodeURIComponent(code)}&state=${encodeURIComponent(params.state)}`;
    } catch (err) {
      console.error('Alexa link error:', err);
      linkedRef.current = false;
      setStatus('error');
      setErrorMsg('Something went wrong writing to the database. Please try again.');
    }
  };

  // Auto-link as soon as user is signed in
  useEffect(() => {
    if (user && status === 'idle') {
      linkAccount();
    }
  }, [user, status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF5F5] via-[#FFF0F0] to-[#FFE8E8] flex items-center justify-center">
        <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'hsl(var(--muted))', borderTopColor: 'hsl(var(--feed-accent))' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF5F5] via-[#FFF0F0] to-[#FFE8E8] flex flex-col items-center justify-center px-6">
      <div className="bg-white rounded-3xl p-8 shadow-xl max-w-sm w-full text-center">
        <p className="text-4xl mb-4">🔗</p>
        <h1 className="text-xl font-bold text-foreground mb-2">Link Baby Tracker to Alexa</h1>

        {status === 'done' && (
          <p className="text-sm font-medium mt-4" style={{ color: 'hsl(150,50%,32%)' }}>
            Account linked! You can close this window and return to the Alexa app.
          </p>
        )}

        {status === 'linking' && (
          <div className="flex items-center justify-center gap-2 mt-4 text-muted-foreground">
            <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'hsl(var(--muted))', borderTopColor: 'hsl(var(--feed-accent))' }} />
            <span className="text-sm">Linking your account…</span>
          </div>
        )}

        {status === 'error' && (
          <>
            <p className="text-sm mt-4 mb-4" style={{ color: 'hsl(var(--destructive))' }}>{errorMsg}</p>
            <button
              onClick={() => { linkedRef.current = false; setStatus('idle'); }}
              className="w-full py-3 text-white rounded-xl font-semibold text-sm"
              style={{ background: 'hsl(var(--feed-accent))' }}
            >
              Try again
            </button>
          </>
        )}

        {status === 'idle' && !user && (
          <>
            <p className="text-sm text-muted-foreground mb-6 mt-2">
              Sign in with the same Google account you use in Baby Tracker.
            </p>
            <button
              onClick={signInWithGoogle}
              className="w-full py-3.5 px-4 bg-white border border-border rounded-xl font-medium text-foreground shadow-sm flex items-center justify-center gap-3"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4" />
                <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853" />
                <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z" fill="#FBBC05" />
                <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335" />
              </svg>
              <span>Continue with Google</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AlexaAuth;
